'use client';
import LazyLoadImage from '@repo/ui/src/components/LazyLoadImage';
import { buildImageSrc } from '@repo/ui/src/functions/image-functions-aspect-ratio';
import dynamic from 'next/dynamic';
import { ModalID, useModal } from '@repo/ui/src/modules/UI';
import { useIsMobile } from '@repo/ui/src/utils/useMediaQuery';
import { MediaType } from '@repo/ui/src/types';

const Markdown = dynamic(() => import('@repo/ui/src/components/Markdown'));

interface TileType {
  image?: Array<MediaType>;
  instagramHandle?: string;
  imgLink?: string;
  recommendedProduct?: any;
  [key: string]: any;
}

interface PhotoGalleryProps {
  title?: string;
  markdownDescription?: string;
  body: TileType[];
  _testID?: string;
}

const PhotoGallery: React.FC<PhotoGalleryProps> = ({
  title,
  markdownDescription,
  body,
  _testID,
}) => {
  const { push } = useModal(ModalID.image);
  const isMobile = useIsMobile();
  const photoQty = body?.length;

  if (!body.length) {
    return null;
  }

  // Grid layouts based on number of photos
  const GRID_LAYOUTS = {
    1: 'tw-grid tw-grid-cols-1 tw-max-w-md tw-mx-auto',
    2: 'tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-max-w-xl md:tw-max-w-3xl tw-mx-auto',
    3: 'tw-grid tw-grid-cols-2 md:tw-grid-cols-3 tw-gap-2',
    4: 'tw-grid tw-grid-cols-2 tw-gap-2',
    5: 'tw-grid tw-grid-cols-2 sm:tw-grid-cols-5 tw-gap-2',
    6: 'tw-grid tw-grid-cols-2 md:tw-grid-cols-3 tw-gap-2',
    7: 'tw-grid tw-grid-cols-2 md:tw-grid-cols-3 tw-gap-2',
    8: 'tw-grid tw-grid-cols-2 md:tw-grid-cols-4 tw-gap-2',
    9: 'tw-grid tw-grid-cols-2 md:tw-grid-cols-3 tw-gap-2',
    default: 'tw-grid tw-grid-cols-2 md:tw-grid-cols-3 lg:tw-grid-cols-4 tw-gap-2',
  };

  // Get the appropriate grid layout
  const gridLayout = GRID_LAYOUTS[photoQty as keyof typeof GRID_LAYOUTS] || GRID_LAYOUTS.default;

  return (
    <div
      className="tw-flex tw-justify-center tw-text-center md:tw-px-8 tw-py-8 md:tw-py-16 rotating-background"
      tabIndex={1}
      data-test-id={`photo_gallery_${_testID}`}
    >
      <div className="tw-px-4">
        {title && <h2 className="tra-text-web-heading-3">{title}</h2>}
        {markdownDescription && (
          <Markdown className="tw-text-center tw-mb-6" bodySize="xLarge">
            {markdownDescription}
          </Markdown>
        )}

        <div
          className={`${gridLayout} tw-mx-auto`}
          style={{ maxWidth: isMobile ? '370px' : '1200px' }}
        >
          {body.map((tile, i) => {
            const imgUrl = tile?.image && buildImageSrc(tile.image[0], 470, 1, 1);
            const instagramHandle = tile?.instagramHandle;
            const image = tile?.image?.[0];
            const imgLink = tile.imgLink;
            const recommendedProduct = tile.recommendedProduct;

            return (
              <div key={i} className={`tw-min-h-40 tw-overflow-hidden`}>
                <button
                  onClick={() => push({ instagramHandle, image, imgLink, recommendedProduct })}
                  className="tw-group tw-w-full tw-h-full tw-relative focus:tw-outline-brand-300 focus:tw-border-2"
                >
                  <LazyLoadImage
                    imgComponent={
                      <img
                        src={imgUrl}
                        alt={
                          tile.image && tile.image.length && tile.image[0].name
                            ? tile.image[0].name
                            : instagramHandle || 'carousel-image'
                        }
                        className="tw-w-full tw-h-full tw-object-cover tw-cursor-pointer"
                      />
                    }
                    className="tw-h-full"
                  />
                  <div className="overlay tw-items-center tw-justify-center tw-hidden group-hover:tw-flex group-focus:tw-flex tw-absolute tw-top-0 tw-bottom-0 tw-left-0 tw-right-0 tw-bg-black/50 tw-cursor-pointer">
                    <span>
                      {tile?.instagramHandle && (
                        <p className="tra-text-web-body-l tw-text-center tw-text-white tw-underline">
                          {tile.instagramHandle}
                        </p>
                      )}
                      <p className="tra-text-web-body-l tw-text-center tw-text-white tw-underline">
                        View
                      </p>
                    </span>
                  </div>
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PhotoGallery;
